import React from "react";
import styled from "styled-components";

interface Column {
  key: string;
  label: string;
  align?: "left" | "right" | "center";
}

interface DataTableProps {
  columns: Column[];
  data: any[];
  renderCell?: (column: Column, row: any, index: number) => React.ReactNode;
  emptyMessage?: string;
  loading?: boolean;
  loadingMessage?: string;
}

const DataTable: React.FC<DataTableProps> = ({
  columns,
  data,
  renderCell,
  emptyMessage = "Nenhum dado encontrado",
  loading = false,
  loadingMessage = "Carregando...",
}) => {
  const defaultRenderCell = (column: Column, row: any) => {
    return row[column.key] || "-";
  };

  const getCellContent = (column: Column, row: any, index: number) => {
    if (renderCell) {
      return renderCell(column, row, index);
    }
    return defaultRenderCell(column, row);
  };

  return (
    <TableContainer>
      <TableWrapper>
        <StyledTable>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={{ textAlign: column.align || "left" }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <BodyScroll>
            {loading && (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ textAlign: "center", padding: "20px" }}
                >
                  {loadingMessage}
                </td>
              </tr>
            )}
            {!loading && data.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ textAlign: "center", padding: "20px" }}
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
            {!loading &&
              data.length > 0 &&
              data.map((row, idx) => (
                <tr key={row.id || idx}>
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      style={{ textAlign: column.align || "left" }}
                    >
                      {getCellContent(column, row, idx)}
                    </td>
                  ))}
                </tr>
              ))}
          </BodyScroll>
        </StyledTable>
      </TableWrapper>
    </TableContainer>
  );
};

export default DataTable;

const TableContainer = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const TableWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: auto;
  min-height: 0;

  /* WebKit scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #2d5bff;
    border-radius: 8px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #2d5bff;
  }

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: #2d5bff transparent;
`;

const StyledTable = styled.table`
  width: 100%;
  min-width: 1200px;
  border-collapse: collapse;

  thead th {
    text-align: left;
    font-family: Inter;
    font-size: 13px;
    font-weight: 600;
    color: #667085;
    background: #f8fafc;
    padding: 14px 16px;
    border-bottom: 1px solid #e5e7eb;
    position: sticky;
    top: 0;
    z-index: 2;
    white-space: nowrap;
  }

  thead th:first-child {
    border-top-left-radius: 12px;
  }
  thead th:last-child {
    border-top-right-radius: 12px;
  }

  tbody td {
    padding: 14px 16px;
    font-family: Inter;
    font-size: 13px;
    color: #111827;
    border-top: 1px solid #e5e7eb;
    background: #ffffff;
  }

  tbody tr:first-child td {
    border-top: none;
  }

  tbody tr:nth-child(even) td {
    background: #f9fafb;
  }
`;

const BodyScroll = styled.tbody``;
